import { apolloContextForShop } from '@core/api/apolloContext';
import {
  getDefaultGraphqlClient,
  getGraphqlClientForShop,
} from '@core/api/graphqlClient';
import { recordCatalogMerge } from '@core/catalog/catalogHealth';
import {
  GET_PRODUCTS,
  GET_PRODUCT_BY_ID,
  GET_PRODUCTS_BY_IDS,
} from '../graphql/queries';
import {
  GetProductsResponse,
  GraphQLProduct,
  GetProductByIdResponse,
} from '../graphql/types';
import { Product, ProductFilterState, ProductVariant } from '../types';
import { checkingImageUrl } from '@shared/utils/imageUrlCheck';
import {
  MULTI_SHOP_ENABLED,
  getEnvShopId,
  getImageBaseForTenantShop,
  isStorefrontCatalogShopExcluded,
} from '@core/config/shops';
import type { TenantShop } from '@core/tenant/types';
import { getTenantShopsSnapshot } from '@core/tenant/registry';
import {
  makeGlobalProductId,
  parseGlobalProductId,
  isGlobalProductId,
} from '@core/utils/globalIds';
import { firstPositivePrice } from '../utils/pricing';
import { normalizeVariantImages } from '../utils/productDisplayImages';
import { CATALOG_FETCH_POLICY } from '@core/api/apolloQueryOptions';
import { CATALOG_PAGE_SIZE } from '@core/catalog/catalogConstants';

/**
 * Normalize CMS/GraphQL image payloads (JSON string, URL string, arrays, or { image | url | src })
 * using the same tenant image base resolution as hero images.
 */
function resolveImagesValue(
  images: unknown,
  imgBase: string | undefined
): string[] {
  if (images === null || images === undefined) return [];

  let raw: unknown = images;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed.startsWith('[')) {
      try {
        raw = JSON.parse(trimmed);
      } catch {
        const one = trimmed ? checkingImageUrl(trimmed, imgBase) : null;
        return one ? [one] : [];
      }
    } else if (trimmed) {
      const one = checkingImageUrl(trimmed, imgBase);
      return one ? [one] : [];
    }
    return [];
  }

  if (Array.isArray(raw)) {
    return raw
      .map(img => {
        if (typeof img === 'string')
          return img ? checkingImageUrl(img, imgBase) : null;
        if (img && typeof img === 'object') {
          const nested = img as Record<string, unknown>;
          const val = nested.image || nested.url || nested.src || nested.file;
          return val ? checkingImageUrl(String(val), imgBase) : null;
        }
        return null;
      })
      .filter(Boolean) as string[];
  }

  if (typeof raw === 'object') {
    const nested = raw as Record<string, unknown>;
    const val = nested.image || nested.url || nested.src || nested.file;
    if (val) {
      const one = checkingImageUrl(String(val), imgBase);
      return one ? [one] : [];
    }
  }

  return [];
}

/** Returns true if an attribute key name represents a color field */
const isColorKey = (key: string): boolean => /colou?r/i.test(key);

/** Returns true if an attribute name (from the attribute map) represents a color attribute */
const isColorAttributeName = (name: string | undefined): boolean =>
  !!name && /colou?r/i.test(name);

/**
 * Normalise a product's attributes into a flat array of variant objects.
 * Handles: array of objects, single plain object, JSON string, or undefined.
 */
const getVariants = (product: Product): ProductVariant[] => {
  const attrs = product.attributes;
  if (!attrs) return [];
  if (Array.isArray(attrs)) return attrs;
  return [];
};

const normalizeVariant = (
  variant: Record<string, unknown>
): Record<string, string> => {
  const normalized: Record<string, string> = {};

  Object.entries(variant).forEach(([key, value]) => {
    if (value == null) return;

    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      normalized[key] = String(value).trim();
      return;
    }

    if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
      const nested = value as Record<string, unknown>;
      if (typeof nested.name === 'string' && nested.value != null) {
        normalized[nested.name.trim()] = String(nested.value).trim();
        return;
      }
      if (nested.value != null && typeof nested.value !== 'object') {
        normalized[key] = String(nested.value).trim();
        return;
      }
      if (Object.keys(nested).length === 1) {
        const firstValue = Object.values(nested)[0];
        if (
          typeof firstValue === 'string' ||
          typeof firstValue === 'number' ||
          typeof firstValue === 'boolean'
        ) {
          normalized[key] = String(firstValue).trim();
          return;
        }
      }
    }
  });

  if (
    'attribute' in variant &&
    typeof variant.attribute === 'object' &&
    variant.attribute !== null &&
    'name' in variant.attribute &&
    'attribute_value' in variant &&
    typeof variant.attribute_value === 'object' &&
    variant.attribute_value !== null &&
    'value' in variant.attribute_value
  ) {
    const attrName = String(variant.attribute.name).trim();
    const attrValue = String(variant.attribute_value.value).trim();
    if (attrName && attrValue) {
      normalized[attrName] = attrValue;
    }
  }

  return normalized;
};

const getVariantColorValue = (
  variant: Record<string, unknown>,
  colorAttrNames: string[] = []
): string | null => {
  const normalized = normalizeVariant(variant);

  for (const knownName of colorAttrNames) {
    for (const key in normalized) {
      if (
        key === knownName ||
        key.toLowerCase() === knownName.toLowerCase() ||
        key.toLowerCase().includes(knownName.toLowerCase())
      ) {
        const val = normalized[key];
        if (val) return val.toUpperCase().trim();
      }
    }
  }

  for (const key in normalized) {
    if (isColorKey(key)) {
      const val = normalized[key];
      if (val) return val.toUpperCase().trim();
    }
  }

  return null;
};

const matchesVariantAttribute = (
  variant: Record<string, unknown>,
  attrId: string,
  attrName: string | undefined,
  values: string[]
): boolean => {
  const normalized = normalizeVariant(variant);
  const upperTargets = values.map(v => v.toUpperCase().trim());
  const skipKeys = new Set([
    'price',
    'images',
    'stocks',
    'bar_code',
    'product_code',
    'offered_price',
    'selling_price',
    'quantity',
    'attribute',
    'attribute_value',
  ]);

  const testValue = (value: string | undefined | null) =>
    value != null && upperTargets.includes(value.toUpperCase().trim());

  for (const key of [attrName, attrId]) {
    if (key && Object.prototype.hasOwnProperty.call(normalized, key)) {
      if (testValue(normalized[key])) return true;
    }
  }

  for (const key in normalized) {
    if (skipKeys.has(key.toLowerCase())) continue;
    const lKey = key.toLowerCase();
    if (
      (attrName &&
        (lKey === attrName.toLowerCase() ||
          lKey.includes(attrName.toLowerCase()))) ||
      (attrId && lKey === attrId.toLowerCase())
    ) {
      if (testValue(normalized[key])) return true;
    }
  }

  return false;
};

/**
 * Apply client-side filters to products
 */
const applyFilters = (
  products: Product[],
  filters?: ProductFilterState,
  attributeMap?: Map<string, string>
): Product[] => {
  let filtered = [...products];

  // ── Search ──────────────────────────────────────────────────────────────────
  if (filters?.search) {
    const q = filters.search.toLowerCase();
    filtered = filtered.filter(
      p =>
        (p.name && p.name.toLowerCase().includes(q)) ||
        (p.description && p.description.toLowerCase().includes(q))
    );
  }

  // ── Attribute filters (Check first for color attributes) ──────────────────────
  const cleanAttributeFilters = Object.entries(
    filters?.attributes ?? {}
  ).reduce(
    (acc, [attrId, selectedValues]) => {
      const values = (selectedValues as string[] | undefined) ?? [];
      if (values.length > 0) {
        acc[attrId] = values;
      }
      return acc;
    },
    {} as Record<string, string[]>
  );

  // ── Category / Sub-category ──────────────────────────────────────────────
  // ALWAYS apply category filters if selected (they work together with color)
  if (filters?.category) {
    const catFilter = filters.category.toLowerCase().trim();
    filtered = filtered.filter(
      p => p.category && p.category.toLowerCase().trim() === catFilter
    );
  }
  if (filters?.subCategory) {
    const subFilter = filters.subCategory.toLowerCase().trim();
    filtered = filtered.filter(
      p => p.subCategory && p.subCategory.toLowerCase().trim() === subFilter
    );
  }

  // ── Price ────────────────────────────────────────────────────────────────────
  if (filters?.minPrice !== undefined) {
    filtered = filtered.filter(p => p.price >= filters.minPrice!);
  }
  if (filters?.maxPrice !== undefined) {
    filtered = filtered.filter(p => p.price <= filters.maxPrice!);
  }

  // ── Attribute filters ────────────────────────────────────────────────────────

  if (Object.keys(cleanAttributeFilters).length === 0) {
    return filtered;
  }

  // ── Phase 1: separate color vs. non-color filter groups ─────────────────────
  //
  // A filter group is treated as a COLOR group when:
  //   (a) attributeMap resolves the attributeId to a name matching /colou?r/i, OR
  //   (b) the attributeId itself matches /colou?r/i (used as name directly)
  //
  // This means even if attributeMap is empty/incomplete, we still catch cases
  // where the ID is something like "color" or "colour" or a UUID that resolves.

  const colorSelectedValues = new Set<string>();
  const colorAttrNames: string[] = [];
  const otherFilters: {
    attrId: string;
    attrName: string | undefined;
    values: string[];
  }[] = [];

  Object.entries(cleanAttributeFilters).forEach(([attrId, values]) => {
    const attrName = attributeMap?.get(attrId);
    if (isColorAttributeName(attrName) || isColorAttributeName(attrId)) {
      values.forEach(v => {
        const trimmed = v.toUpperCase().trim();
        if (trimmed) colorSelectedValues.add(trimmed);
      });
      if (attrName) colorAttrNames.push(attrName);
    } else {
      otherFilters.push({ attrId, attrName, values });
    }
  });

  const productMatchesVariantFilters = (variant: ProductVariant): boolean => {
    if (colorSelectedValues.size > 0) {
      const colorVal = getVariantColorValue(variant, colorAttrNames);
      if (!colorVal || !colorSelectedValues.has(colorVal)) {
        return false;
      }
    }

    return otherFilters.every(({ attrId, attrName, values }) =>
      matchesVariantAttribute(variant, attrId, attrName, values)
    );
  };

  const seen = new Set<string>();

  filtered = filtered.reduce<Product[]>((acc, product) => {
    const variants = getVariants(product);

    // Handle products with no variants but might have direct color attributes
    if (variants.length === 0) {
      // Check if product matches color filters directly
      if (colorSelectedValues.size > 0) {
        let colorVal: string | null = null;

        // First try to find color in product's direct attributes
        const productAttrs = product.attributes;
        if (
          productAttrs &&
          Array.isArray(productAttrs) &&
          productAttrs.length > 0
        ) {
          const mockVariant = productAttrs[0];
          colorVal = getVariantColorValue(
            mockVariant as unknown as Record<string, unknown>,
            colorAttrNames
          );
        }

        // If no color found in attributes, try extracting from product name
        if (!colorVal && product.name) {
          const colorKeywords = [
            'red',
            'blue',
            'green',
            'yellow',
            'black',
            'white',
            'pink',
            'brown',
            'navy',
            'ash',
            'purple',
            'orange',
            'maroon',
            'grey',
            'gray',
            'beige',
            'khaki',
            'teal',
            'olive',
            'cream',
            'rose',
            'rani',
            'lite',
          ];
          const nameLower = product.name.toLowerCase();

          for (const color of colorKeywords) {
            if (nameLower.includes(color)) {
              colorVal = color.toUpperCase();
              break;
            }
          }
        }

        if (colorVal && colorSelectedValues.has(colorVal)) {
          const dedupeKey = `${product.id}__${colorVal}`;

          if (!seen.has(dedupeKey)) {
            seen.add(dedupeKey);
            acc.push(product);
          }
        }
      }
      return acc;
    }

    // Handle products with variants
    variants.forEach(variant => {
      if (!productMatchesVariantFilters(variant)) return;

      const colorVal =
        getVariantColorValue(variant, colorAttrNames) ?? 'DEFAULT';
      const dedupeKey =
        colorSelectedValues.size > 0
          ? `${product.id}__${colorVal}`
          : product.id;

      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);

      const enriched = { ...product };
      const vImg = variant.images;
      if (Array.isArray(vImg) && vImg.length > 0) {
        enriched.imageUrl = vImg as string[];
      }
      acc.push(enriched);
    });

    return acc;
  }, []);

  const dedupedFiltered = Array.from(
    new Map(
      filtered.map(product => {
        const imageKey = Array.isArray(product.imageUrl)
          ? product.imageUrl.join('|')
          : (product.imageUrl ?? '');
        return [`${product.id}__${imageKey}`, product];
      })
    ).values()
  );

  return dedupedFiltered;
};

/**
 * Convert GraphQL product to our Product type
 */
const mapGraphQLProductToProduct = (
  gqlProduct: GraphQLProduct,
  lineShopId?: string
): Product => {
  const tenantShops = getTenantShopsSnapshot();
  const imgBase = getImageBaseForTenantShop(lineShopId, tenantShops);
  // Handle images - can be string, array, or null
  let images = gqlProduct.images;

  // If it's a JSON string, parse it first
  if (typeof images === 'string' && images.trim().startsWith('[')) {
    try {
      images = JSON.parse(images);
    } catch (e) {
      // Keep as string if not valid JSON
    }
  }

  let imageUrl: string | string[] | undefined;
  if (images) {
    if (Array.isArray(images)) {
      const resolvedImages = images
        .map(img => {
          if (typeof img === 'string') return checkingImageUrl(img, imgBase);
          if (img && typeof img === 'object') {
            // Handle objects like { image: "..." } or { url: "..." }
            const nested = img as Record<string, unknown>;
            const val = nested.image || nested.url || nested.src || nested.file;
            return val ? checkingImageUrl(String(val), imgBase) : null;
          }
          return null;
        })
        .filter(Boolean) as string[];
      imageUrl = resolvedImages.length > 0 ? resolvedImages : undefined;
    } else if (typeof images === 'string') {
      imageUrl = checkingImageUrl(images, imgBase);
    }
  }

  // Handle images in attributes (variants)
  let attributes: ProductVariant[] = [];

  // First, try to get attributes from mst_product_variants relation (New Structure)
  if (
    gqlProduct.mst_product_variants &&
    Array.isArray(gqlProduct.mst_product_variants)
  ) {
    attributes = gqlProduct.mst_product_variants.map(v => {
      const price = firstPositivePrice(v.price);
      const offeredPrice = firstPositivePrice(v.offered_price);

      const baseAttrs =
        typeof v.attributes === 'string'
          ? JSON.parse(v.attributes)
          : (v.attributes ?? {});

      // Prefer images on the variant row; if empty, keep images embedded in `attributes` JSON
      // (previously an empty column overwrote nested images and broke PDP / cards).
      const fromColumn = resolveImagesValue(v.images, imgBase);
      const fromNested =
        fromColumn.length > 0
          ? fromColumn
          : resolveImagesValue(
              (baseAttrs as Record<string, unknown>).images,
              imgBase
            );

      return {
        ...baseAttrs,
        id: v.id,
        price,
        offered_price: offeredPrice,
        images: fromNested,
        stocks: v.mst_stock?.stock_count || 0,
      };
    });
  }
  // Fallback to the old attributes field if no variants found
  else if (gqlProduct.attributes) {
    let rawAttrs: unknown = gqlProduct.attributes;
    if (typeof rawAttrs === 'string') {
      try {
        const parsed = JSON.parse(rawAttrs);
        rawAttrs = Array.isArray(parsed) ? parsed : [parsed];
      } catch (e) {
        rawAttrs = [];
      }
    }

    if (Array.isArray(rawAttrs)) {
      attributes = (rawAttrs as ProductVariant[]).map(variant => {
        const parsed = resolveImagesValue(variant.images, imgBase);
        if (parsed.length > 0) {
          return { ...variant, images: parsed };
        }
        if (variant.images && Array.isArray(variant.images)) {
          return {
            ...variant,
            images: variant.images
              .map((img: unknown) => checkingImageUrl(String(img), imgBase))
              .filter(Boolean) as string[],
          };
        }
        return variant;
      });
    }
  }

  const variantPrices = attributes
    .map(variant =>
      firstPositivePrice(
        variant.offered_price,
        variant.selling_price,
        variant.price
      )
    )
    .filter(variantPrice => variantPrice > 0);
  const variantPrice =
    variantPrices.length > 0 ? Math.min(...variantPrices) : 0;
  const productPrice = firstPositivePrice(
    gqlProduct.offered_price,
    gqlProduct.selling_price,
    gqlProduct.base_price,
    gqlProduct.mrp_price
  );
  const price = firstPositivePrice(variantPrice, productPrice);

  // Use stocks or quantity for stock count
  const stock = gqlProduct.stocks ?? gqlProduct.quantity ?? 0;

  // Get category name from relation or direct field
  const category =
    gqlProduct.mst_category?.category || gqlProduct.category || '';

  // Get sub category name from relation or direct field
  const subCategory =
    gqlProduct.mst_sub_category?.sub_name ||
    gqlProduct.sub_category ||
    undefined;

  // When variants exist, `imageUrl` must come from variant photos only so listings / PDP / cart agree
  // with the SKU (never an unrelated main `products.images` thumbnail).
  let catalogHeroImageUrl: string | string[] | undefined = imageUrl;

  if (attributes.length > 0) {
    const fromFirst = normalizeVariantImages(attributes[0]);
    if (fromFirst.length > 0) {
      catalogHeroImageUrl =
        fromFirst.length === 1 ? fromFirst[0] : [...fromFirst];
    } else {
      for (let i = 1; i < attributes.length; i++) {
        const urls = normalizeVariantImages(attributes[i]);
        if (urls.length > 0) {
          catalogHeroImageUrl = urls.length === 1 ? urls[0] : [...urls];
          break;
        }
      }
    }
  }

  const localId = gqlProduct.id;
  const id =
    MULTI_SHOP_ENABLED && lineShopId
      ? makeGlobalProductId(lineShopId, localId)
      : localId;

  return {
    id,
    shopId: lineShopId ?? undefined,
    name: gqlProduct.name || '',
    description: gqlProduct.description || '',
    price,
    imageUrl: catalogHeroImageUrl,
    visible: gqlProduct.visible ?? undefined,
    category,
    stock,
    attributes: attributes, // Store attributes for filtering
    subCategory, // Store sub category for filtering
    topSelling: gqlProduct.top_selling ?? false,
    trending: gqlProduct.trending_products ?? false,
    createdAt: gqlProduct.created_at ?? undefined,
  };
};

/**
 * GraphQL Product Service
 * Note: The products query may need adjustment based on your actual Hasura schema
 */
export const graphqlProductService = {
  /**
   * Fetch one page of products for a single shop.
   */
  fetchProductsPage: async (
    shopId: string,
    offset = 0,
    limit = CATALOG_PAGE_SIZE
  ): Promise<Product[]> => {
    try {
      const client = getGraphqlClientForShop(shopId);
      const { data, error } = await client.query<GetProductsResponse>({
        query: GET_PRODUCTS,
        variables: { shopId, limit, offset },
        fetchPolicy: CATALOG_FETCH_POLICY,
        context: apolloContextForShop(shopId),
      });
      if (error) {
        console.error(
          `[products] shop "${shopId}" GraphQL error:`,
          error.message || error
        );
        return [];
      }
      const rows = (data?.mst_product ?? []).filter(
        row => row.visible === true
      );
      return rows.map(p =>
        mapGraphQLProductToProduct(p, MULTI_SHOP_ENABLED ? shopId : undefined)
      );
    } catch (e) {
      console.error(`[products] shop "${shopId}" request failed:`, e);
      return [];
    }
  },

  /**
   * Load catalog pages until a shop returns fewer than `pageSize` rows or `maxPages` is hit.
   */
  loadCatalogForShops: async (
    tenantShops: TenantShop[],
    options?: { maxPages?: number; pageSize?: number }
  ): Promise<Product[]> => {
    const shops = tenantShops?.length ? tenantShops : getTenantShopsSnapshot();
    const pageSize = options?.pageSize ?? CATALOG_PAGE_SIZE;
    const maxPages = options?.maxPages ?? 20;

    const loadShop = async (shopId: string): Promise<Product[]> => {
      const acc: Product[] = [];
      for (let page = 0; page < maxPages; page += 1) {
        const offset = page * pageSize;
        const slice = await graphqlProductService.fetchProductsPage(
          shopId,
          offset,
          pageSize
        );
        acc.push(...slice);
        if (slice.length < pageSize) break;
      }
      return acc;
    };

    try {
      if (!MULTI_SHOP_ENABLED) {
        const sid = shops[0]?.id ?? getEnvShopId();
        const products = await loadShop(sid);
        recordCatalogMerge({
          shopIds: [sid],
          productCountByShop: { [sid]: products.length },
          totalMerged: products.length,
        });
        return products;
      }

      const results = await Promise.all(
        shops.map(async s => ({
          id: s.id,
          products: await loadShop(s.id),
        }))
      );
      const merged = results.flatMap(r => r.products);
      recordCatalogMerge({
        shopIds: shops.map(s => s.id),
        productCountByShop: Object.fromEntries(
          results.map(r => [r.id, r.products.length])
        ),
        totalMerged: merged.length,
      });
      return merged;
    } catch (err) {
      console.error('Error loading catalog:', err);
      return [];
    }
  },

  /**
   * Get all products (paginated internally; filters applied client-side).
   */
  getAllProducts: async (tenantShops?: TenantShop[]): Promise<Product[]> => {
    return graphqlProductService.loadCatalogForShops(tenantShops ?? [], {
      maxPages: 1,
    });
  },

  /**
   * Get a single product by ID (UUID or composite `shopId::uuid` in multi mode)
   */
  getProductsByIds: async (ids: string[]): Promise<Product[]> => {
    if (ids.length === 0) return [];

    try {
      const sid = getEnvShopId();
      const result =
        await getDefaultGraphqlClient().query<GetProductByIdResponse>({
          query: GET_PRODUCTS_BY_IDS,
          variables: { shopId: sid, ids },
          fetchPolicy: CATALOG_FETCH_POLICY,
          context: apolloContextForShop(sid),
        });

      if (result.error) {
        console.error('GraphQL error (GetProductsByIds):', result.error);
        return [];
      }

      const rows = result.data?.mst_product ?? [];
      return rows
        .filter(row => row.visible === true)
        .map(row => mapGraphQLProductToProduct(row, undefined));
    } catch (err) {
      console.error('Error fetching products by ids:', err);
      return [];
    }
  },

  getProductById: async (id: string): Promise<Product> => {
    try {
      if (MULTI_SHOP_ENABLED && isGlobalProductId(id)) {
        const parsed = parseGlobalProductId(id);
        if (!parsed) throw new Error('Invalid product id');
        if (isStorefrontCatalogShopExcluded(parsed.shopId)) {
          throw new Error('Product not found');
        }
        const client = getDefaultGraphqlClient();
        const result = await client.query<GetProductByIdResponse>({
          query: GET_PRODUCT_BY_ID,
          variables: {
            id: parsed.localProductId,
            shopId: parsed.shopId,
          },
          fetchPolicy: CATALOG_FETCH_POLICY,
          context: apolloContextForShop(parsed.shopId),
        });
        if (result.error) {
          throw new Error(result.error.message || 'Failed to fetch product');
        }
        const row = result.data?.mst_product?.[0];
        if (!row || row.visible !== true) throw new Error('Product not found');
        return mapGraphQLProductToProduct(row, parsed.shopId);
      }

      if (MULTI_SHOP_ENABLED) {
        const catalog = getTenantShopsSnapshot();
        for (const shop of catalog) {
          const client = getDefaultGraphqlClient();
          const result = await client.query<GetProductByIdResponse>({
            query: GET_PRODUCT_BY_ID,
            variables: { id, shopId: shop.id },
            fetchPolicy: CATALOG_FETCH_POLICY,
            context: apolloContextForShop(shop.id),
          });
          const row = result.data?.mst_product?.[0];
          if (row && row.visible === true)
            return mapGraphQLProductToProduct(row, shop.id);
        }
        throw new Error('Product not found');
      }

      const sid = getEnvShopId();
      const result =
        await getDefaultGraphqlClient().query<GetProductByIdResponse>({
          query: GET_PRODUCT_BY_ID,
          variables: {
            id,
            shopId: sid,
          },
          fetchPolicy: CATALOG_FETCH_POLICY,
          context: apolloContextForShop(sid),
        });

      if (result.error) {
        console.error('GraphQL error:', result.error);
        throw new Error(result.error.message || 'Failed to fetch product');
      }

      if (
        !result.data ||
        !result.data.mst_product ||
        result.data.mst_product.length === 0
      ) {
        throw new Error('Product not found');
      }

      const row = result.data.mst_product[0];
      if (!row || row.visible !== true) {
        throw new Error('Product not found');
      }
      return mapGraphQLProductToProduct(row, undefined);
    } catch (err) {
      const error = err as Error;
      console.error('Error fetching product from GraphQL:', error);
      throw error;
    }
  },

  /**
   * Apply filters to products (client-side)
   */
  applyFilters,
};
